import { Button, TableProps } from "antd";
import { useEffect, useState } from "react";
import CVPdfIcon from "../../assets/icons/CVPdfIcon";
import ViewIcon from "../../assets/icons/ViewIcon";
import { useJobShortlistOrRejectMutation } from "../../features/employee/employeeApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { formatDate } from "../../lib/time";
import { toast } from "../../lib/toast";
import { JobApplication } from "../../types";
import { addKeyInArray } from "../../utils/utils";
import ConfirmModal from "../ui/ConfirmModal";
import CustomTable from "../ui/CustomTable";
import ApplicationDetails from "./ApplicationDetails";

type Props = {
  loading: boolean;
  data?: JobApplication[];
};

export const applicationStatusList = {
  new: {
    value: "New",
    style: "status-dark",
  },
  short_listed: {
    value: "Short Listed",
    style: "status-link",
  },
  rejected: {
    value: "Rejected",
    style: "status-danger",
  },
};

type Status = "short_listed" | "rejected";
type TApplicationDetails = {
  details: string;
  inquiry: string;
  cv: string;
  title: string;
  id: number;
  status: Status;
  email?: string;
  cover?: string;
  phone?: string;
  address?: string;
};

export default function ApplicationList({ loading, data }: Props) {
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { open, handleOpen, handleClose } = useModal<
    Status | TApplicationDetails | undefined
  >(reRender);
  const [jobShortlistOrReject, { isLoading, isError, error, isSuccess }] =
    useJobShortlistOrRejectMutation();
  useError(
    isError,
    error,
    open?.data === "rejected"
      ? "Application reject fail"
      : "Application short fail"
  );

  const handleApproveOrReject = (status: Status) => {
    const newValues = {
      ids: selectedData,
      status,
    };

    jobShortlistOrReject(newValues);
  };

  const columns: TableProps<JobApplication>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Applicant Name",
      dataIndex: "full_name",
      render: (val) => val || "n/a",
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val) => val || "n/a",
    },

    {
      title: "Phone No.",
      dataIndex: "mobile",
      render: (val) => val || "n/a",
    },
    {
      title: "Applied Date",
      dataIndex: "created_at",
      render: (val) => (val ? formatDate(val) : "n/a"),
    },
    {
      title: "CV",
      dataIndex: "cv_link",
      render: (val) => (
        <a
          href={val}
          target="_blank"
          download
          rel="noreferrer"
          className="flex items-center gap-1.5 cursor-pointer"
        >
          <CVPdfIcon color="#B18A00" />
          <span className="text-[#B18A00] text-sm font-medium">View CV</span>
        </a>
      ),
    },
    {
      title: "Status",
      render: (_, record) => {
        const status = applicationStatusList[record?.status];
        return <span className={status?.style}>{status?.value}</span>;
      },
    },
    {
      title: "Action",
      render: (_, record) => {
        return (
          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: {
                  title: record?.full_name,
                  email: record?.email,
                  phone: record?.mobile,
                  address: record?.address,
                  cv: record?.cv_link,
                  details: record?.inquiry,
                  inquiry: record?.inquiry,
                  id: record?.id,
                  status: record?.status as Status,
                },
              })
            }
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (isSuccess) {
      toast(
        "success",
        open?.data === "rejected"
          ? "Application rejected successfully"
          : "Application shorted successfully"
      );
      handleClose(true);
      setSelectedData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  function reRender() {
    setSelectedData([]);
  }

  return (
    <div>
      {open?.type === "UPDATE" && (
        <ConfirmModal
          open={open?.open}
          handleClose={handleClose}
          handleOk={() =>
            open?.data && handleApproveOrReject(open?.data as Status)
          }
          confirmTitle="Are you sure?"
          questionText={
            open?.data === "short_listed"
              ? "Do you want short listed them?"
              : "Do you want reject them?"
          }
          okText="Yes! Confirm"
          okLoading={isLoading}
        />
      )}

      {open?.type === "VIEW" && (
        <ApplicationDetails
          open={open?.open}
          handleClose={handleClose}
          cv={(open?.data as TApplicationDetails)?.cv}
          title={(open?.data as TApplicationDetails)?.title}
          details={(open?.data as TApplicationDetails)?.inquiry}
          id={(open?.data as TApplicationDetails)?.id}
          status={(open?.data as TApplicationDetails)?.status}
          email={(open?.data as TApplicationDetails)?.email}
          phone={(open?.data as TApplicationDetails)?.phone}
          address={(open?.data as TApplicationDetails)?.address}
        />
      )}

      {selectedData?.length ? (
        <div className="my-4 flex items-center gap-4">
          <h5 className="text-black text-base font-semibold">
            Selected: {selectedData?.length}
          </h5>
          <Button
            type="primary"
            onClick={() => handleOpen({ data: "short_listed", type: "UPDATE" })}
            disabled={isLoading}
          >
            Short List
          </Button>
          <Button
            type="primary"
            className="!text-[#FF3838] disabled:!text-[#FF3838]/70 disabled:!bg-[#FFF5F4] !bg-[#FFF5F4] !border-[##FFF5F4]"
            onClick={() => handleOpen({ data: "rejected", type: "UPDATE" })}
            disabled={isLoading}
          >
            Reject
          </Button>
        </div>
      ) : null}
      <CustomTable
        loading={loading}
        columns={columns}
        dataSource={addKeyInArray(data)}
        className="header-table-color"
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => {
            return {
              disabled:
                record?.status === "rejected" ||
                record?.status === "short_listed",
            };
          },
          columnWidth: 40,
        }}
      />
    </div>
  );
}
